import { Chip, Typography } from '@mui/material'
import type { Assignment } from '../api/generated/Assignment'
import type { AssignmentStatus } from '../api/generated/AssignmentStatus'
import type { Attendance } from '../api/generated/Attendance'

const statuses: Record<
  AssignmentStatus,
  { label: string; color: 'default' | 'success' | 'error' | 'warning' }
> = {
  accepted: { label: 'Validé', color: 'success' },
  rejected: { label: 'Retiré', color: 'error' },
  withdrawn: { label: 'Désisté', color: 'default' },
  offered: { label: 'Remplacement demandé', color: 'warning' },
}

export const statusLabel = (status: AssignmentStatus) => statuses[status].label

export function StatusChip({ status }: { status: AssignmentStatus }) {
  const { label, color } = statuses[status]
  return <Chip size="small" variant="outlined" label={label} color={color} />
}

const attendances: Record<
  Exclude<Attendance, 'not_requested'>,
  { label: string; color: 'success' | 'warning' }
> = {
  requested: { label: 'Présence non confirmée', color: 'warning' },
  confirmed: { label: 'Présence confirmée', color: 'success' },
  checked_in: { label: 'En poste', color: 'success' },
}

/** Attendance of an accepted volunteer, once their shift's reminder asked for it or they checked in. */
function attendanceOf({ status, attendance }: Pick<Assignment, 'status' | 'attendance'>) {
  return status === 'accepted' && attendance !== 'not_requested' ? attendances[attendance] : null
}

export const attendanceLabel = (assignment: Pick<Assignment, 'status' | 'attendance'>) =>
  attendanceOf(assignment)?.label ?? null

export function AttendanceChip({ assignment }: { assignment: Assignment }) {
  const attendance = attendanceOf(assignment)
  return attendance && <Chip size="small" label={attendance.label} color={attendance.color} />
}

export function OverlapWarning({ assignment }: { assignment: Assignment }) {
  if (!assignment.overlaps) return null
  return (
    <Typography variant="caption" color="warning.main" sx={{ width: '100%' }}>
      Déjà sur un autre créneau à la même heure
    </Typography>
  )
}

export const volunteerName = (person: Pick<Assignment, 'display_name' | 'email' | 'phone'>) =>
  person.display_name ?? person.email ?? person.phone ?? 'Bénévole'
